/* Balmohk · Pop-up de newsletter (sections/bm-newsletter-popup.liquid).
   Pasos: alta → cumpleaños → éxito. El alta es el formulario nativo de clientes de Shopify; se envía por fetch
   una sola vez, con el cumpleaños (si lo hay) como etiqueta. Si Shopify pide el captcha o falla la red, se envía
   el formulario de verdad y, al volver, el pop-up se abre en el estado que devolvió Shopify. */
if (!customElements.get('bm-newsletter-popup')) {
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  class BmNewsletterPopup extends HTMLElement {
    #abort = new AbortController();
    #sending = false;
    #sent = false;

    connectedCallback() {
      const { signal } = this.#abort;
      this.dialog = this.querySelector('[data-bm-nlp-dialog]');
      this.form = this.querySelector('form');
      this.holder = this.querySelector('[data-bm-nlp-state]');
      if (!(this.dialog instanceof HTMLDialogElement) || !this.form || !this.holder) return;

      // El hCaptcha de Shopify escucha el submit del formulario y, aunque acá se cancele, pide igual el desafío
      // (POST a api.hcaptcha.com) y lo abre detrás del velo, inerte. Por eso la validación corta antes: en el clic de
      // «Suscribirse» (Enter en un campo también lo dispara) y, si el submit llega por otro lado, en captura desde
      // acá, antes que cualquier listener del formulario. Con datos válidos el submit sigue como siempre.
      this.querySelector('[data-bm-nlp-subscribe]')?.addEventListener('click', (event) => this.#guard(event), { signal });
      this.addEventListener('submit', (event) => this.#guard(event), { capture: true, signal });
      this.form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
      this.querySelector('[data-bm-nlp-send]')?.addEventListener('click', () => this.#send(true), { signal });
      this.querySelector('[data-bm-nlp-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
      this.querySelector('[data-bm-nlp-code]')?.addEventListener('click', (event) => this.#copyCode(event.currentTarget), { signal });
      // Clic en el velo (fuera de la tarjeta) cierra
      this.dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === this.dialog) this.dialog.close();
        },
        { signal }
      );
      // Cerrar en el paso del cumpleaños no pierde el alta: se envía sin la fecha
      this.dialog.addEventListener(
        'close',
        () => {
          if (this.#step() === 'birthday' && !this.#sent) this.#send(false);
        },
        { signal }
      );
      // Un error se borra al corregir el campo
      this.form.addEventListener('input', (event) => this.#clearError(event.target), { signal });
      this.form.addEventListener('change', (event) => this.#clearError(event.target), { signal });
      this.querySelector('[data-bm-nlp-birthday]')?.addEventListener('input', (event) => this.#maskDate(event.target), { signal });

      // Vuelta de un envío nativo (sin fetch): Shopify redirige con el id del formulario en el hash
      if (this.holder.hasAttribute('data-bm-nlp-posted') && window.location.hash === `#${this.form.id}`) {
        this.#remember();
        this.#open();
        return;
      }

      if (this.dataset.autoOpen === 'true' && !this.#seen()) {
        const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
        this.timer = window.setTimeout(() => {
          if (document.querySelector('dialog[open]')) return; // no se monta sobre otro modal (carrito, buscador…)
          this.#remember();
          this.#open();
        }, delay);
      }
    }

    disconnectedCallback() {
      this.#abort.abort();
      window.clearTimeout(this.timer);
    }

    #seen() {
      try {
        return window.localStorage.getItem(this.dataset.storageKey) === '1';
      } catch {
        return false;
      }
    }

    #remember() {
      try {
        window.localStorage.setItem(this.dataset.storageKey, '1');
      } catch {
        /* sin almacenamiento: se vuelve a ofrecer en la próxima visita */
      }
    }

    #open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      // El foco va al título del paso, no al primer campo (en mobile abriría el teclado)
      this.#focusHeading();
    }

    #focusHeading() {
      const heading = this.#currentStep()?.querySelector('h2');
      if (!heading) return;
      if (heading.id) this.dialog.setAttribute('aria-labelledby', heading.id);
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }

    #step() {
      return this.holder.dataset.bmNlpState;
    }

    #currentStep() {
      return this.querySelector(`[data-bm-nlp-step="${this.#step()}"]`);
    }

    #show(step) {
      this.holder.dataset.bmNlpState = step;
      for (const panel of this.querySelectorAll('[data-bm-nlp-step]')) {
        panel.hidden = panel.dataset.bmNlpStep !== step;
      }
      this.#focusHeading();
    }

    /* ── Errores: en un hueco reservado debajo del campo, sin mover el resto (103:8702) ── */
    #setError(name, message) {
      const error = this.querySelector(`[data-bm-nlp-error="${name}"]`);
      const control =
        name === 'email'
          ? this.querySelector('[data-bm-nlp-email]')
          : name === 'consent'
            ? this.querySelector('[data-bm-nlp-consent]')
            : this.querySelector('[data-bm-nlp-birthday]');
      if (error) {
        error.textContent = message || '';
        error.hidden = !message;
      }
      if (control) {
        if (message) control.setAttribute('aria-invalid', 'true');
        else control.removeAttribute('aria-invalid');
      }
    }

    #clearError(target) {
      if (!(target instanceof HTMLInputElement)) return;
      if (target.matches('[data-bm-nlp-email]')) this.#setError('email', '');
      if (target.matches('[data-bm-nlp-consent]') && target.checked) this.#setError('consent', '');
      if (target.matches('[data-bm-nlp-birthday]')) this.#setError('birthday', '');
    }

    #validate() {
      const email = this.querySelector('[data-bm-nlp-email]');
      const consent = this.querySelector('[data-bm-nlp-consent]');
      let firstInvalid = null;
      if (email instanceof HTMLInputElement) {
        const value = email.value.trim();
        if (!value) this.#setError('email', this.dataset.errorRequired);
        else if (!EMAIL.test(value)) this.#setError('email', this.dataset.errorEmail);
        else this.#setError('email', '');
        if (!value || !EMAIL.test(value)) firstInvalid = email;
      }
      if (consent instanceof HTMLInputElement) {
        this.#setError('consent', consent.checked ? '' : this.dataset.errorRequired);
        if (!consent.checked) firstInvalid ||= consent;
      }
      firstInvalid?.focus();
      return !firstInvalid;
    }

    /* ── Cumpleaños: DD/MM/AAAA → etiqueta prefijo + MM-DD ── */
    #maskDate(input) {
      const digits = input.value.replace(/\D/g, '').slice(0, 8);
      const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean);
      input.value = parts.join('/');
    }

    #birthdayTag() {
      const input = this.querySelector('[data-bm-nlp-birthday]');
      const prefix = this.dataset.birthdayPrefix;
      if (!(input instanceof HTMLInputElement) || !prefix || !input.value.trim()) return '';
      const match = input.value.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) return null;
      const [, day, month, year] = match;
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      const valid =
        date.getFullYear() === Number(year) &&
        date.getMonth() === Number(month) - 1 &&
        date.getDate() === Number(day) &&
        date < new Date();
      return valid ? `${prefix}${month}-${day}` : null;
    }

    #tags(birthdayTag) {
      const tags = [this.dataset.tagBase || 'newsletter'];
      for (const box of this.querySelectorAll('[data-bm-nlp-interest]')) {
        if (box instanceof HTMLInputElement && box.checked && box.value) tags.push(box.value);
      }
      if (birthdayTag) tags.push(birthdayTag);
      const field = this.querySelector('[data-bm-nlp-tags]');
      if (field instanceof HTMLInputElement) field.value = tags.join(', ');
    }

    /* Sin envío en curso y con datos válidos el alta sigue; si no, se corta acá y no llega a haber submit */
    #guard(event) {
      if (event.type === 'submit' && event.target !== this.form) return;
      if (!this.#sending && this.#validate()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    // Llega ya validado (#guard)
    #onSubmit(event) {
      this.#tags('');
      // Sin paso de cumpleaños configurado: se envía directo
      if (!this.dataset.birthdayPrefix || !this.querySelector('[data-bm-nlp-step="birthday"]')) {
        event.preventDefault();
        this.#send(false);
        return;
      }
      event.preventDefault();
      this.#show('birthday');
    }

    async #send(withBirthday) {
      if (this.#sending || this.#sent) return;
      let birthdayTag = '';
      if (withBirthday) {
        birthdayTag = this.#birthdayTag();
        if (birthdayTag === null) {
          this.#setError('birthday', this.dataset.errorDate);
          this.querySelector('[data-bm-nlp-birthday]')?.focus();
          return;
        }
      }
      this.#tags(birthdayTag);
      this.#sending = true;
      const button = this.querySelector(withBirthday ? '[data-bm-nlp-send]' : '[data-bm-nlp-subscribe]');
      button?.setAttribute('aria-busy', 'true');
      const closing = !this.dialog.open;

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new URLSearchParams(new FormData(this.form)),
          headers: { Accept: 'text/html' },
          keepalive: closing,
        });
        // Captcha de Shopify: hay que pasar por su página
        if (response.url.includes('/challenge')) throw new Error('challenge');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById(this.form.id)?.querySelector('[data-bm-nlp-state]');
        if (!fresh) throw new Error('sin respuesta del formulario');

        if (fresh.dataset.bmNlpState === 'success') {
          this.#sent = true;
          if (!closing) this.#show('success');
          return;
        }
        // Error devuelto por Shopify: se vuelve al alta con su mensaje
        const message = fresh.querySelector('[data-bm-nlp-error="email"]')?.textContent?.trim();
        if (closing) return;
        this.#show('form');
        this.#setError('email', message || this.dataset.errorGeneric);
        this.querySelector('[data-bm-nlp-email]')?.focus();
      } catch {
        if (closing) return;
        // Envío nativo: Shopify resuelve (captcha incluido) y al volver se abre con su estado
        this.#sent = true;
        HTMLFormElement.prototype.submit.call(this.form);
      } finally {
        this.#sending = false;
        button?.removeAttribute('aria-busy');
      }
    }

    async #copyCode(button) {
      const code = button.dataset.bmNlpCode;
      const label = button.querySelector('[data-bm-nlp-code-label]');
      try {
        await navigator.clipboard.writeText(code);
        if (label) {
          label.textContent = this.dataset.copied || code;
          window.setTimeout(() => (label.textContent = code), 2000);
        }
      } catch {
        window.prompt('', code);
      }
    }
  }

  customElements.define('bm-newsletter-popup', BmNewsletterPopup);
}
